<template lang="pug">
div
	div.tw-flex.tw-items-start(class="tw-gap-[120px] emd:tw-flex-col emd:tw-gap-2 emd:tw-mb-10")
		div.tw-flex.tw-flex-col(class="emd:tw-flex-row")
			span(v-if='passenger && passenger.data.name' class="emd:tw-font-bold emd:tw-text-xl") {{passenger.data.name}} {{passenger.data.surname}}
			span(v-else) {{$t("common.passenger", 0)}} {{ travellerIndex + 1 }}
			span.tw-hidden.tw-mx-3(class="emd:tw-block") -
			span(class="emd:tw-font-bold") {{ travellerCategoryLabel }}
		div.tw-flex-1(class="emd:tw-w-full")
			div.tw-flex.tw-gap-4.tw-justify-stretch(class="emd:tw-flex-col")
				div.tw-flex-1: v-select(
					:model-value='travellerTrainCatalogSelection'
					@update:model-value="changeSelectedCatalogService($event)"
					:items='catalogServiceSelectOptions'
					item-title="label"
					item-value="id"
					item-disabled="disabled"
					single-line
					hide-details
					hide-no-data
					:menu-icon="source=='bookingChange' ? '' : '$dropdown'"
					:readonly="source=='bookingChange'"
				)
				div.tw-flex-1: v-select(
					:disabled="!travellerTrainCatalogSelection"
					:model-value='travellerTrainOfferSelection'
					@update:model-value="changeSelectedOffer($event, true)"
					:items='offeredServiceSelectOptions'
					item-title="label"
					item-value="id"
					item-disabled="disabled"
					single-line
					hide-details
					:menu-icon="source=='bookingChange' ? '' : '$dropdown'"
					:readonly="source=='bookingChange'"
				)
			template(v-if="selectedOffer && selectedOffer.offer")
				div.tw-mt-2.tw-rounded.tw-p-4(class='tw-bg-[#ededed]' v-if='!isMobile')
					div.tw-flex
						span.tw-font-bold(class='tw-text-[18px]') {{ selectedOffer.offer.display_name }}
						span {{  }}
						span.tw-flex-1
						span.tw-font-bold(class='tw-text-[#E53935]') {{ formattedPrice }}
					offer-description(
						:selected-offer="selectedOffer"
						:offers-messages="offersMessages"
						:passenger="passenger"
						:travel-solution-selection-state="travelSolutionSelectionState"
						:train-id="trainId"
						:traveller-xml-id="travellerXmlId"
					)
				div.tw-mt-2.tw-rounded.tw-p-4(class='tw-bg-[#ededed]' v-else)
					div.tw-flex(@click="expandOfferDescription = !expandOfferDescription")
						v-icon(icon="mdi-eye" class="tw-mr-3")
						span.tw-font-bold(class='tw-text-[18px]') {{ selectedOffer.offer.display_name }}
						span {{  }}
						span.tw-flex-1
						span.tw-font-bold(class='tw-text-[#E53935]') {{ formattedPrice }}
					v-expand-transition
						offer-description(
							v-if="expandOfferDescription"
							:selected-offer="selectedOffer"
							:offers-messages="offersMessages"
							:passenger="passenger"
							:travel-solution-selection-state="travelSolutionSelectionState"
							:train-id="trainId"
							:traveller-xml-id="travellerXmlId"
						)
</template>
	
<script setup>
import { printCurrencyValue } from '@/common-scripts/utilities';
import { useBookingStore, makeCustomizedOfferedServiceSelection } from '@/stores/booking';
import {computed, inject, ref, toRefs, watch} from 'vue';
import {get as _get} from 'lodash-es';
import {useI18n} from "vue-i18n";
import OfferDescription from "@/sale/components/tab2_travel_solutions/customize-offer/offer-description.vue";


const booking = useBookingStore();
const props = defineProps({
	travellerIndex: Number,
	travellerXmlId: String,
	travellerOffersMap: Object,
	trainId: String,
	globalOffer: {
		type: Object,
		default: null
	}
});
const emit = defineEmits(['thisOfferIsForAll']);
const {globalOffer} = toRefs(props);
const travelSolution = inject('solution');
const source = inject('source');
const {setSelectedCustomizedOffer, state: travelSolutionSelectionState} = inject('travelSolutionSelection');
const i18n = useI18n();
const passenger = computed(() => {
	return booking.passengers.find(p => p.data.xmlId == props.travellerXmlId);
});
const travellerCategoryLabel = computed(() => {
	if(passenger.value && passenger.value.data.type == 'CHILDREN') return i18n.t("common.children", 1).toUpperCase();
	return i18n.t("common.adult", 1).toUpperCase();
});
const travellerTrainCatalogSelection = computed(() => {
	return _get(travelSolutionSelectionState.selectedCustomizedOffers, [props.trainId, props.travellerXmlId, 'catalogServiceId']);
});
const travellerTrainOfferSelection = computed(() => {
	return _get(travelSolutionSelectionState.selectedCustomizedOffers, [props.trainId, props.travellerXmlId, 'offerId']);
});
const catalogServiceSelectOptions = computed(() => {
	const options = [];
	for (const catalogService of props.travellerOffersMap) {
		if(!booking.servicesMap.catalog_services[catalogService.id]) continue;
		options.push({
			id: catalogService.id,
			label: booking.servicesMap.catalog_services[catalogService.id].display_name
		})
	}
	return options;
});
const selectedCatalogServiceItem = computed(() => {
	return props.travellerOffersMap && props.travellerOffersMap.find(o => o.id == travellerTrainCatalogSelection.value);
});
const offeredServiceSelectOptions = computed(() => {
	if(!selectedCatalogServiceItem.value) return [];
	const options = [];
	for (const offerService of selectedCatalogServiceItem.value.offers) {
		if(!booking.servicesMap.offers[offerService.offerId]) continue;
		options.push({
			id: offerService.offerId,
			label: booking.servicesMap.offers[offerService.offerId].display_name,
			disabled: !offerService.selectable, // not work as documentation, need to customize slot
			data: offerService,
			offer: booking.servicesMap.offers[offerService.offerId],
			service: booking.servicesMap.catalog_services[selectedCatalogServiceItem.value.id],
			offereds: offerService.offers.map(s => travelSolution.value.offeredServices[s])
		});
	}
	return options;
});
const selectedOffer = computed(() => {
	if(!travellerTrainCatalogSelection.value || !travellerTrainOfferSelection.value) return null;
	return offeredServiceSelectOptions.value.find(o => o.id == travellerTrainOfferSelection.value);
});

const offersMessages = computed(() => {
	const messageDuplicated = [];
	const messages = [];
	if(!selectedOffer.value) return messages;
	for (const offered of selectedOffer.value.offereds) {
		if(!offered || !offered.offeredServiceMessages) continue;
		for (const offeredServiceMessage of offered.offeredServiceMessages) {
			if(messageDuplicated.includes(offeredServiceMessage.description)) continue;
			if(['SEATMAP', 'POINTS_MAX_CASHBACK', 'POINTS_STEP_CASHBACK'].includes(offeredServiceMessage.type)) continue;
			messages.push(offeredServiceMessage);
			messageDuplicated.push(offeredServiceMessage.description);
		}
		if(booking.evaluatedOffers && booking.evaluatedOffers[offered.id]){
			let evaluatedMessages = _get(booking.evaluatedOffers[offered.id], 'bookingInfo.reportItem.messages');
			if(!Array.isArray(evaluatedMessages)) evaluatedMessages = [evaluatedMessages];
			for (const evaluatedMessage of evaluatedMessages) {
				if(messageDuplicated.includes(evaluatedMessage)) continue;
				messages.push({description: evaluatedMessage, evaluated:true});
				messageDuplicated.push(evaluatedMessage);
			}
		}
	}
	return messages;
});
const formattedPrice = computed(() => {
	return typeof(selectedOffer.value.data.totalPrice) == 'number' ? printCurrencyValue(selectedOffer.value.data.totalPrice) : '-';
});
const offersToBeSetAtAll = ['FrecciaFAMILY'];
const isMobile = inject('isMobile');
const expandOfferDescription = ref(!isMobile.value)


function changeSelectedCatalogService(catalogServiceId){
	if(!travelSolutionSelectionState.selectedCustomizedOffers[props.trainId]) travelSolutionSelectionState.selectedCustomizedOffers[props.trainId] = {};
	if(travelSolutionSelectionState.selectedCustomizedOffers[props.trainId][props.travellerXmlId] && travelSolutionSelectionState.selectedCustomizedOffers[props.trainId][props.travellerXmlId].catalogServiceId == catalogServiceId) return;
	const offerService = props.travellerOffersMap && props.travellerOffersMap.find(o => o.id == catalogServiceId).offers[0];
	const offeredService = travelSolution.value.offeredServices[offerService.offers[0]];
	travelSolutionSelectionState.selectedCustomizedOffers[props.trainId][props.travellerXmlId] = makeCustomizedOfferedServiceSelection(offeredService, booking.servicesMap);
	booking.setEvaluatedSolution();
}
function changeSelectedOffer(offerId, isTheSource = false){
	if(!selectedCatalogServiceItem.value) return;
	const offerService = selectedCatalogServiceItem.value.offers.find(o => o.offerId == offerId);
	if(!offerService) return;
	setSelectedCustomizedOffer(props.trainId, props.travellerXmlId, offerId, selectedCatalogServiceItem.value.id, offerService.offers);

	if (isTheSource && booking.servicesMap.offers[offerId] && offersToBeSetAtAll.includes(booking.servicesMap.offers[offerId].display_name)) {
		emit('thisOfferIsForAll', {
			offerId: travellerTrainOfferSelection.value,
			serviceId: travellerTrainCatalogSelection.value
		});
	}
}


watch(globalOffer, () => {
	changeSelectedCatalogService(globalOffer.value.serviceId);
	changeSelectedOffer(globalOffer.value.offerId);
});
</script>